import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import api from "../../services/api";
import CheesecakeCard from "./CheesecakeCard";
import SharedCartPreview from "../../pages/Cart/SharedCartPreview";
import SharedBanner from "../../components/SharedBanner";

const CheesecakesPage = () => {
  const [cheesecakes, setCheesecakes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hideBanner, setHideBanner] = useState(false);

  useEffect(() => {
    document.title = "Cheesecakes";
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cakesRes, sizesRes] = await Promise.all([
        api.get("/cheesecakes"),
        api.get("/sizes"),
      ]);
      setCheesecakes(cakesRes.data);
      setSizes(sizesRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSharedSizeData = () => {
    const usedSizeIds = new Set(
      cheesecakes.flatMap((c) => c.sizes.map((s) => s.$oid || s))
    );

    return sizes.filter((s) => usedSizeIds.has(s._id));
  };

  const sharedSizes = getSharedSizeData();

  return (
    <Container disableGutters maxWidth="xl">
      {!hideBanner && <SharedBanner onClose={() => setHideBanner(true)} />}

      {/* Page Header */}
      <Box sx={{ mt: 4, mb: 2, px: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Our Cheesecakes
        </Typography>
        <Typography variant="body1">
          Choose from our delicious, pre-defined cheesecakes. No customization
          needed — just select your favorite and enjoy!
        </Typography>
      </Box>

      {/* Shared Info Section */}
      {sharedSizes.length > 0 && (
        <Card sx={{ mb: 4, p: 2, borderRadius: 2, backgroundColor: "#FFF3E0" }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Available Sizes
            </Typography>
            {sharedSizes.map((size) => (
              <Box key={size._id} sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>{size.displayName}</strong> — ${size.price.toFixed(2)}{" "}
                  • Serves {size.serves}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Cheesecake Cards */}
      <Grid container spacing={3}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: "300px",
            }}>
            <CircularProgress color="primary" size={50} />
            <Typography variant="h6" sx={{ mt: 2, color: "#515151" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          cheesecakes
            .filter((cake) => cake.isActive !== false)
            .map((cake) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={cake._id}
                sx={{ mx: { xs: 2, sm: 0 }, mb: { xs: 4, md: 0 } }}>
                <CheesecakeCard
                  cake={cake}
                  sizes={sizes}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  setShowCartPreview={setShowCartPreview}
                  isAvailable={cake.isAvailable !== false}
                />
              </Grid>
            ))
        )}
      </Grid>

      <SharedCartPreview
        show={showCartPreview}
        setShowCartPreview={setShowCartPreview}
      />
    </Container>
  );
};

export default CheesecakesPage;
